import { memo, useCallback, useState } from 'react';
import {
  IconBack,
  IconChevronDown,
  InformationalScreen,
  Stack,
  Text,
} from '@adc/parallax-component-library';
import { LocalDate } from '@js-joda/core';

import {
  BirthdayInput,
  FormContainer,
  Input,
  MainContainer,
  PhoneInput,
} from '~/components/utility';
import { buildTestId } from '~/utilities/buildTestId';
import {
  formatCloudDateToFrontend,
  formatDateFromCloud,
} from '~/utilities/formatCloudDate';
import i18n from '~/utilities/i18n';
import mediator from '~/utilities/mediator';

type Props = {
  patient: Partial<Patient> | undefined;
  onBack: (
    initialValues: Partial<Patient> | undefined,
    currentValues: Partial<Patient>,
  ) => void;
};

const testID = 'account-info-adult';

const AccountInfoAdult = ({ patient, onBack }: Props) => {
  const [values, setValues] = useState({
    ...patient,
    dateOfBirth: formatCloudDateToFrontend(patient?.dateOfBirth) ?? '',
  });

  const handleOnBack = useCallback(() => {
    onBack(patient, values);
  }, [patient, values, onBack]);

  const onUpdateEmail = useCallback(() => {
    mediator.publish('router:redirect', '/update-email');
  }, []);

  const onUpdatePhone = useCallback(() => {
    mediator.publish('router:redirect', '/update-phone');
  }, []);

  const handleDateChange = useCallback((date: LocalDate) => {
    // added as required for the input, this is disabled always
    setValues((prevValues) => ({
      ...prevValues,
      dateOfBirth: date.toString(),
    }));
  }, []);

  const initialDoB = formatDateFromCloud(values.dateOfBirth);

  return (
    <MainContainer testID={testID}>
      <InformationalScreen
        testID={buildTestId({ testID, suffix: 'screen' })}
        topNavigationProps={{
          title: i18n.t('AccountInformationPersonalInfo.title'),
          buttonStart: {
            element: <IconBack testID="nav-back" />,
            onPress: handleOnBack,
            label: 'Go Back',
          },
          testID: 'nav-back',
        }}
      >
        <InformationalScreen.Page
          testID={buildTestId({ testID, suffix: 'page' })}
        >
          <InformationalScreen.Content>
            <Text marginTop={-8}>
              {i18n.t('AccountInformationPersonalInfo.content.subtitle')}
            </Text>
            <FormContainer>
              <Stack onPress={onUpdateEmail}>
                <Input
                  aria-label={i18n.t(
                    'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.emailAddress.label',
                  )}
                  label={i18n.t(
                    'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.emailAddress.label',
                  )}
                  testID={buildTestId({ testID, suffix: ['email', 'input'] })}
                  inputMode="text"
                  value={values.email}
                  onFocus={onUpdateEmail}
                  inlineIconConfig={{
                    testID: buildTestId({
                      testID,
                      suffix: ['email', 'input', 'icon'],
                    }),
                    icon: (
                      <span style={{ transform: 'rotate(-90deg)' }}>
                        <IconChevronDown color="$interactive.core.enabled" />
                      </span>
                    ),
                    'aria-label': 'Change Email Address',
                    onPress: onUpdateEmail,
                  }}
                />
              </Stack>
              <Stack onPress={onUpdatePhone}>
                <PhoneInput
                  testID={buildTestId({ testID, suffix: ['phone', 'input'] })}
                  label={i18n.t(
                    'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.phone.label',
                  )}
                  placeholder={i18n.t(
                    'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.phone.placeholder',
                  )}
                  inputMode="text"
                  value={values.phoneNumber}
                  onFocus={onUpdatePhone}
                />
              </Stack>
              <Input
                aria-label={i18n.t(
                  'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.firstName.label',
                )}
                placeholder={i18n.t(
                  'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.firstName.placeholder',
                )}
                label={i18n.t(
                  'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.firstName.label',
                )}
                testID={buildTestId({
                  testID,
                  suffix: ['first', 'name', 'input'],
                })}
                value={values.firstName}
                disabled
              />
              <Input
                aria-label={i18n.t(
                  'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.lastName.label',
                )}
                placeholder={i18n.t(
                  'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.lastName.placeholder',
                )}
                label={i18n.t(
                  'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.lastName.label',
                )}
                testID={buildTestId({
                  testID,
                  suffix: ['last', 'name', 'input'],
                })}
                inputMode="text"
                value={values.lastName}
                disabled
              />
              <BirthdayInput
                testID={buildTestId({ testID, suffix: ['dob', 'input'] })}
                disabled
                initialDate={initialDoB}
                onDateChange={handleDateChange}
              />
              <Input
                aria-label={i18n.t(
                  'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.country.label',
                )}
                label={i18n.t(
                  'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.country.label',
                )}
                testID={buildTestId({ testID, suffix: ['country', 'input'] })}
                value={i18n.t(
                  `Global.microcopy.countries.${values.country as 'US'}`,
                )}
                disabled
              />
            </FormContainer>
          </InformationalScreen.Content>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default memo(AccountInfoAdult);
